/**
 * Main App Component for MEV-Med System
 * Root component with routing, theme provider, and global providers
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import InitColorSchemeScript from '@mui/material/InitColorSchemeScript';
import { SnackbarProvider } from 'notistack';
import { HelmetProvider } from 'react-helmet-async';

// Theme
import { mevTheme } from './theme';

// O que cada rota exige, declarado nela mesma — issue #408.
import { pode, semPorta } from './authorization/pode';

// Pages
import {
  LoginPage,
  HomePage,
  ContextChoicePage,
  LideraAIPage,
  CompaniesListPage,
  CompanyCreatePage,
  CompanyEditPage,
  CompanyDetailsPage,
  DepartmentsListPage,
  DepartmentCreatePage,
  DepartmentEditPage,
  UnitsListPage,
  ProvidersListPage,
  ProviderCreatePage,
  ProviderEditPage,
  ProviderDetailsPage,
  ProviderWeeklySchedulePage,
  ProfessionalsListPage,
  ProfessionalCreatePage,
  ProfessionalDetailsPage,
  ProfessionalEditPage,
  EmployeesListPage,
  EmployeeFormPage,
  EmployeeDetailsPage,
  EmployeeDashboardPage,
  EmployeeImportExportPage,
  AppointmentsListPage,
  AppointmentFormPage,
  AppointmentDetailsPage,
  AppointmentResponsePage,
  ReferralResponsePage,
  ReferralReportsPage,
  PendingAppointmentsPage,
  ScheduleSlotsListPage,
  MedicalConsultationListPage,
  ConsultationHistoryPage,
  ConsultationPage,
  HistoryImportPage,
} from './pages';

// Display Panel Page (public TV display)
import { DisplayPanelPage } from './pages/display';

import {
  OccupationsListPage,
  OccupationCreatePage,
  OccupationEditPage
} from './pages/occupations';

// StandardText Pages
import {
  StandardTextListPage,
  StandardTextCreatePage,
  StandardTextEditPage
} from './pages/standard-texts';

// Document Emission Pages
import { DocumentEmissionPage, DocumentHistoryPage } from './pages/documents';

// OccupationalRisk Pages
import {
  OccupationalRiskListPage,
  OccupationalRiskCreatePage,
  OccupationalRiskEditPage,
  OccupationalRiskDetailsPage
} from './pages/occupational-risks';

// OccupationalExam Pages
import {
  OccupationalExamListPage,
  OccupationalExamCreatePage,
  OccupationalExamEditPage
} from './pages/occupational-exams';

// Painel de absenteismo
import { AbsenteeismPage } from './pages/absenteeism';
import { TurnoverPage, TurnoverPresentationPage } from './pages/turnover';
import { ImplantacaoPage } from './pages/implantacao';
import { PricingSettingsPage, ProviderPricingPage } from './pages/pricing';

// Exame toxicologico do motorista (Lei 13.103/2015)
import { ToxicologicalExamsListPage } from './pages/toxicological-exams';
import { ConsultantListPage } from './pages/consultants';
// Plano de ação — a lista de planos e o plano aberto (issue #359)
import { ActionPlanDetailPage, ActionPlanListPage } from './pages/action-plan';
import { BenefitModulesPage } from './pages/benefit-modules';
import { VencimentosPage } from './pages/vencimentos';
import {
  DenunciaListPage,
  DenunciaDetailPage,
  DenunciaPublicaPage,
  DenunciaStatusPage,
} from './pages/denuncia';
import {
  AssinaturaListPage,
  AssinaturaDetailPage,
  AssinarDocumentoPage,
  VerificarAssinaturaPage,
} from './pages/assinaturas';
import VerificarDocumentoPage from './pages/verificacao/VerificarDocumentoPage';
import ResponderAnamnesePage from './pages/anamnese/ResponderAnamnesePage';
import { EntregaDeEpiListPage, EntregaDeEpiFormPage, EntregaDeEpiDetailPage } from './pages/epis/entregas';
import {
  ProposalListPage,
  ProposalFormPage,
  ProposalDocumentPage,
  ProposalPublicPage,
} from './pages/proposals';

// ExamApplication Pages
import { ExamApplicationPage } from './pages/exam-applications';

// ProviderExam Pages
import { ProviderExamsPage } from './pages/provider-exams';

// RiskApplication Pages
import { RiskApplicationPage } from './pages/risk-applications';

// RiskExamMigration Pages
import { RiskExamMigrationPage } from './pages/risk-exam-migration';

// SourceApplication Pages
import { SourceApplicationPage } from './pages/source-applications';

// ApplicationGroup Pages
import { ApplicationGroupPage } from './pages/application-groups';

// SourceGenerator Pages
import {
  SourceGeneratorListPage,
  SourceGeneratorCreatePage,
  SourceGeneratorEditPage
} from './pages/source-generators';

// MeasurementEquipment Pages
import {
  MeasurementEquipmentListPage,
  MeasurementEquipmentCreatePage,
  MeasurementEquipmentEditPage
} from './pages/measurement-equipments';

// EnvironmentalMeasurement Pages
import {
  EnvironmentalMeasurementListPage,
  EnvironmentalMeasurementCreatePage,
  EnvironmentalMeasurementEditPage
} from './pages/environmental-measurements';

// Training Pages
import {
  TrainingListPage,
  TrainingCreatePage,
  TrainingEditPage
} from './pages/trainings';

// TrainingType Pages
import {
  TrainingTypeListPage,
  TrainingTypeCreatePage,
  TrainingTypeEditPage
} from './pages/training-types';

// EPI Pages
import {
  EPIListPage,
  EPICreatePage,
  EPIEditPage
} from './pages/epis';

// EPC Pages
import {
  EPCListPage,
  EPCCreatePage,
  EPCEditPage
} from './pages/epcs';

// Medical Leaves Pages
import {
  MedicalLeavesListPage,
  MedicalLeavesApplyPage
} from './pages/medical-leaves';

// Disabilities Pages
import { DisabilitiesListPage, DisabilityFormPage } from './pages/disabilities';

// CID Codes Pages
import { CidCodesListPage, CidCodesFormPage } from './pages/cid-codes';

// Exam Convocations Pages
import { ExamConvocationPage } from './pages/exam-convocations';

// Professional Schedules Pages
import { ProfessionalSchedulesListPage } from './pages/professional-schedules';

// Holidays Pages
import { HolidaysListPage } from './pages/holidays';

// WhatsApp Pages
import { WhatsAppConfigPage } from './pages/whatsapp';

// Certificate Pages
import {
  CertificateListPage,
} from './pages/certificates';

// Settings Pages
import { EmailConfigPage } from './pages/settings';

// Reception Pages
import { ReceptionListPage } from './pages/reception';

// Public Pages
import { KitAtendimentoPublicPage, KitExpiredPage } from './pages/public';
import { PortalEntradaPage, PortalPage } from './pages/portal';

// RBAC Pages
import {
  RoleListPage,
  RoleFormPage,
  RolePermissionsPage,
  PermissionListPage,
} from './pages/rbac';

// Support Pages (Central de Atendimento)
import { SupportCenterPage } from './pages/support';

// Auth Pages (password management)
import {
  ForgotPasswordPage,
  ResetPasswordPage,
  ChangePasswordPage,
  MfaVerifyPage,
} from './pages/auth';

// Profile Pages
import { UserProfilePage, MfaSettingsPage } from './pages/profile';

// Users Pages
import {
  UsersListPage,
  UserCreatePage,
  UserEditPage,
  UserDetailsPage,
} from './pages/users';

// CIPAA (Election) Pages
import {
  CipaaListPage,
  CipaaCreatePage,
  CipaaEditPage,
  CipaaDetailsPage,
  CipaaPublicVotePage,
  CipaaPublicCandidaturePage,
} from './pages/cipaa';

// Psychosocial Pages
import {
  PsychosocialDashboardPage,
  PsychosocialFormsPage,
  PsychosocialFormBuilderPage,
  PsychosocialCampaignsPage,
  PsychosocialCampaignFormPage,
  PsychosocialCampaignDetailPage,
  PsychosocialCampaignDashboardPage,
  PsychosocialResultsPage,
  PsychosocialRespondPage,
  PsychosocialClubeRiscoPage,
} from './pages/psychosocial';

// CAT (Comunicacao de Acidente de Trabalho) Pages
import {
  CatsListPage,
  CatCreatePage,
  CatEditPage,
  CatViewPage,
} from './pages/cats';

// Financial Pages
import {
  FinancialDashboard,
  InvoiceListPage,
  InvoiceDetailPage,
  PlatformFeeConfigPage,
} from './pages/financial';

// Help Center Pages
import { HelpCenterPage } from './pages/help';

// Components
import { ProtectedRoute } from './components';
import { AuthLogoutListener } from './components/AuthLogoutListener';

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={mevTheme}>
        <InitColorSchemeScript attribute="data" defaultMode="system" />
        <CssBaseline enableColorScheme />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          dense
          preventDuplicate
        >
          <Router>
            <AuthLogoutListener />
            <Routes>
              {/* Public Routes */}
              {/* Portal do Trabalhador — fora do chassi autenticado: quem entra aqui nao
                  tem conta, nao tem menu e nao deve cair na tela de login do sistema. */}
              <Route path="/portal/entrar" element={<PortalEntradaPage />} />
              <Route path="/portal/entrar/:token" element={<PortalPage />} />
              <Route path="/portal" element={<PortalPage />} />

              {/* A proposta pelo link que o comercial manda. Mesmo motivo do portal: quem abre
                  e o prospecto, nao tem conta, e nao pode cair na tela de login. */}
              <Route path="/proposta/:token" element={<ProposalPublicPage />} />

              {/* Canal de Denúncia — público e anônimo: quem denuncia não tem conta e não pode
                  cair no login. O token da empresa e o protocolo são a credencial. */}
              <Route path="/canal-de-denuncia/:token" element={<DenunciaPublicaPage />} />
              <Route path="/acompanhar-denuncia" element={<DenunciaStatusPage />} />

              {/* Assinatura eletrônica — o signatário não tem conta: o link pessoal é a credencial.
                  A verificação é o endereço do QR code do protocolo, aberto por qualquer pessoa. */}
              <Route path="/assinar/:token" element={<AssinarDocumentoPage />} />
              <Route path="/verificar" element={<VerificarAssinaturaPage />} />
              <Route path="/verificar/:codigo" element={<VerificarAssinaturaPage />} />
              {/* O QR do ASO, da guia e do prontuário — aberto por qualquer pessoa, sem login. */}
              <Route path="/verificar-documento" element={<VerificarDocumentoPage />} />
              <Route path="/verificar-documento/:codigo" element={<VerificarDocumentoPage />} />
              {/* A anamnese que o trabalhador responde pelo celular ou no tablet da recepção:
                  o link pessoal é a credencial, e o CPF é conferido antes das perguntas. */}
              <Route path="/anamnese/:token" element={<ResponderAnamnesePage />} />

              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/mfa-verify" element={<MfaVerifyPage />} />

              {/*
                T3 — a escolha de contexto na entrada. Fica dentro de ProtectedRoute (quem
                escolhe já está autenticado) e é a única rota que a guarda deixa abrir com
                `precisaEscolher` de pé; qualquer outra redireciona para cá.
              */}
              <Route
                path="/escolher-contexto"
                element={
                  <ProtectedRoute
                    exige={semPorta('é aqui que o contexto ativo é escolhido, antes de existir manifesto')}
                  >
                    <ContextChoicePage />
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/home"
                element={
                  <ProtectedRoute
                    exige={semPorta('a tela inicial da sessão: ela só mostra o que a pessoa já alcança')}
                  >
                    <HomePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/vencimentos"
                element={
                  <ProtectedRoute exige={pode('vencimento.read')}>
                    <VencimentosPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/modulos-do-app"
                element={
                  <ProtectedRoute exige={pode('benefit_module.read')}>
                    <BenefitModulesPage />
                  </ProtectedRoute>
                }
              />

              {/* Companies Routes */}
              <Route
                path="/companies"
                element={
                  <ProtectedRoute exige={pode('company.read')}>
                    <CompaniesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/create"
                element={
                  <ProtectedRoute exige={pode('company.create')}>
                    <CompanyCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:id/edit"
                element={
                  <ProtectedRoute exige={pode('company.update')}>
                    <CompanyEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:id"
                element={
                  <ProtectedRoute exige={pode('company.read')}>
                    <CompanyDetailsPage />
                  </ProtectedRoute>
                }
              />

              {/* Providers Routes */}
              <Route
                path="/providers"
                element={
                  <ProtectedRoute exige={pode('provider.read')}>
                    <ProvidersListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/providers/create"
                element={
                  <ProtectedRoute exige={pode('provider.create')}>
                    <ProviderCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/providers/:id"
                element={
                  <ProtectedRoute exige={pode('provider.read')}>
                    <ProviderDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/providers/:id/edit"
                element={
                  <ProtectedRoute exige={pode('provider.update')}>
                    <ProviderEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/providers/weekly-schedule"
                element={
                  <ProtectedRoute exige={pode('provider.read')}>
                    <ProviderWeeklySchedulePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/providers/:providerId/exams"
                element={
                  <ProtectedRoute exige={pode('provider_exam.read')}>
                    <ProviderExamsPage />
                  </ProtectedRoute>
                }
              />

              {/* Professionals Routes */}
              <Route
                path="/professionals"
                element={
                  <ProtectedRoute exige={pode('professional.read')}>
                    <ProfessionalsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/professionals/create"
                element={
                  <ProtectedRoute exige={pode('professional.create')}>
                    <ProfessionalCreatePage />
                  </ProtectedRoute>
                }
              />
              {/*
                A ficha vem ANTES de `/:id/edit` só por leitura: o React Router
                casa por especificidade, não por ordem. A listagem já apontava
                "Visualizar" para cá — sem esta rota o catch-all mandava o
                usuário para a home sem avisar.
              */}
              <Route
                path="/professionals/:id"
                element={
                  <ProtectedRoute exige={pode('professional.read')}>
                    <ProfessionalDetailsPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Enquanto a rota não declarava a permissão de alterar, quem só
                tinha `professional.read` chegava aqui pela URL e via a tela de
                escrita inteira — a listagem escondia o lápis, a rota não.
              */}
              <Route
                path="/professionals/:id/edit"
                element={
                  <ProtectedRoute exige={pode('professional.update')}>
                    <ProfessionalEditPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Departments Routes */}
              <Route
                path="/departments"
                element={
                  <ProtectedRoute exige={pode('department.read')}>
                    <DepartmentsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/departments/create"
                element={
                  <ProtectedRoute exige={pode('department.create')}>
                    <DepartmentCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/departments/:id/edit"
                element={
                  <ProtectedRoute exige={pode('department.update')}>
                    <DepartmentEditPage />
                  </ProtectedRoute>
                }
              />

              {/* Units Routes */}
              <Route
                path="/units"
                element={
                  <ProtectedRoute exige={pode('unit.read')}>
                    <UnitsListPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Occupations Routes */}
              <Route
                path="/occupations"
                element={
                  <ProtectedRoute exige={pode('occupation.read')}>
                    <OccupationsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/occupations/create"
                element={
                  <ProtectedRoute exige={pode('occupation.create')}>
                    <OccupationCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/occupations/:id/edit"
                element={
                  <ProtectedRoute exige={pode('occupation.update')}>
                    <OccupationEditPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Employees Routes */}
              <Route
                path="/employees"
                element={
                  <ProtectedRoute exige={pode('employee.read')}>
                    <EmployeesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/dashboard"
                element={
                  <ProtectedRoute exige={pode('employee.read')}>
                    <EmployeeDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/import-export"
                element={
                  <ProtectedRoute exige={pode('employee.import')}>
                    <EmployeeImportExportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/history-import"
                element={
                  <ProtectedRoute exige={pode('appointment.import')}>
                    <HistoryImportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/new"
                element={
                  <ProtectedRoute exige={pode('employee.create')}>
                    <EmployeeFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/:id"
                element={
                  <ProtectedRoute exige={pode('employee.read')}>
                    <EmployeeDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/:id/edit"
                element={
                  <ProtectedRoute exige={pode('employee.update')}>
                    <EmployeeFormPage />
                  </ProtectedRoute>
                }
              />

              {/* Appointments Routes */}
              <Route
                path="/appointments"
                element={
                  <ProtectedRoute exige={pode('appointment.read')}>
                    <AppointmentsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments/new"
                element={
                  <ProtectedRoute exige={pode('appointment.create')}>
                    <AppointmentFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments/:id/referral-reports"
                element={
                  <ProtectedRoute exige={pode('referral_report.manage')}>
                    <ReferralReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments/:id"
                element={
                  <ProtectedRoute exige={pode('appointment.read')}>
                    <AppointmentDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments/:id/edit"
                element={
                  <ProtectedRoute exige={pode('appointment.update')}>
                    <AppointmentFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments/pending"
                element={
                  <ProtectedRoute exige={pode('appointment.view_pending')}>
                    <PendingAppointmentsPage />
                  </ProtectedRoute>
                }
              />
              {/* Public Appointment Response Route (no auth required) */}
              <Route
                path="/appointment-response/:id/:token"
                element={<AppointmentResponsePage />}
              />

              {/* Public Referral Response Route (no auth required) */}
              <Route
                path="/agendamentos/:id/referral/:action/token/:token"
                element={<ReferralResponsePage />}
              />

              {/* Public Display Panel Route (TV display, no auth required) */}
              <Route
                path="/painel"
                element={<DisplayPanelPage />}
              />

              {/* Public Kit de Atendimento Routes (no auth required) */}
              <Route
                path="/kit/expirado"
                element={<KitExpiredPage />}
              />
              <Route
                path="/kit/:token"
                element={<KitAtendimentoPublicPage />}
              />

              {/* Schedule Slots Routes */}
              <Route
                path="/schedule-slots"
                element={
                  <ProtectedRoute exige={pode('schedule_slot.read')}>
                    <ScheduleSlotsListPage />
                  </ProtectedRoute>
                }
              />

              {/* Reception Routes */}
              <Route
                path="/reception"
                element={
                  <ProtectedRoute exige={pode('reception.read')}>
                    <ReceptionListPage />
                  </ProtectedRoute>
                }
              />

              {/* Medical Consultation Routes */}
              <Route
                path="/medical-consultations/:appointmentId"
                element={
                  <ProtectedRoute exige={pode('medical-records.read')}>
                    <ConsultationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-consultations"
                element={
                  <ProtectedRoute exige={pode('medical-records.read')}>
                    <MedicalConsultationListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/consultation-history"
                element={
                  <ProtectedRoute exige={pode('medical-records.read')}>
                    <ConsultationHistoryPage />
                  </ProtectedRoute>
                }
              />

              {/* StandardTexts Routes */}
              <Route
                path="/standard-texts"
                element={
                  <ProtectedRoute exige={pode('standard_text.read')}>
                    <StandardTextListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/standard-texts/new"
                element={
                  <ProtectedRoute exige={pode('standard_text.create')}>
                    <StandardTextCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/standard-texts/:id/edit"
                element={
                  <ProtectedRoute exige={pode('standard_text.update')}>
                    <StandardTextEditPage />
                  </ProtectedRoute>
                }
              />

              {/* Document Emission Routes */}
              <Route
                path="/documents/history"
                element={
                  <ProtectedRoute exige={pode('document_emission.read')}>
                    <DocumentHistoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents/history/emitir"
                element={
                  <ProtectedRoute exige={pode('document_emission.read')}>
                    <DocumentEmissionPage />
                  </ProtectedRoute>
                }
              />
              {/* O endereço antigo da emissão continua respondendo, para quem tem link ou
                  favorito guardado. Redirecionamento, e não tela protegida: passando pela
                  guarda, ele cairia na mesma recusa por vínculo que a mudança de rota veio
                  consertar. */}
              <Route
                path="/documents/emission"
                element={<Navigate to="/documents/history/emitir" replace />}
              />

              {/* OccupationalRisks Routes */}
              <Route
                path="/occupational-risks"
                element={
                  <ProtectedRoute exige={pode('occupational_risk.read')}>
                    <OccupationalRiskListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/occupational-risks/new"
                element={
                  <ProtectedRoute exige={pode('occupational_risk.create')}>
                    <OccupationalRiskCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/occupational-risks/:id/edit"
                element={
                  <ProtectedRoute exige={pode('occupational_risk.update')}>
                    <OccupationalRiskEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/occupational-risks/:id"
                element={
                  <ProtectedRoute exige={pode('occupational_risk.read')}>
                    <OccupationalRiskDetailsPage />
                  </ProtectedRoute>
                }
              />

              {/* Painel de absenteismo */}
              <Route
                path="/absenteismo"
                element={
                  <ProtectedRoute exige={pode('medical_leaves.read')}>
                    <AbsenteeismPage />
                  </ProtectedRoute>
                }
              />
              {/* Precificação: a configuração da casa e a tabela por clínica. */}
              <Route
                path="/precificacao"
                element={
                  <ProtectedRoute exige={pode('pricing.view')}>
                    <PricingSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/precificacao/clinicas"
                element={
                  <ProtectedRoute exige={pode('pricing.view')}>
                    <ProviderPricingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rotatividade"
                element={
                  <ProtectedRoute exige={pode('employee.read')}>
                    <TurnoverPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/implantacao"
                element={
                  <ProtectedRoute exige={pode('company.read')}>
                    <ImplantacaoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lidera-ia"
                element={
                  <ProtectedRoute exige={pode('lidera_ai.use')}>
                    <LideraAIPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rotatividade/apresentacao/:companyId"
                element={
                  <ProtectedRoute exige={pode('employee.read')}>
                    <TurnoverPresentationPage />
                  </ProtectedRoute>
                }
              />

              {/* Exame toxicologico do motorista */}
              <Route
                path="/exames-toxicologicos"
                element={
                  <ProtectedRoute exige={pode('toxicological_exam.read')}>
                    <ToxicologicalExamsListPage />
                  </ProtectedRoute>
                }
              />

              {/* OccupationalExams Routes */}
              <Route
                path="/occupational-exams"
                element={
                  <ProtectedRoute exige={pode('occupational_exam.read')}>
                    <OccupationalExamListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/occupational-exams/new"
                element={
                  <ProtectedRoute exige={pode('occupational_exam.create')}>
                    <OccupationalExamCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/occupational-exams/:id/edit"
                element={
                  <ProtectedRoute exige={pode('occupational_exam.update')}>
                    <OccupationalExamEditPage />
                  </ProtectedRoute>
                }
              />

              {/* ExamApplication Routes */}
              <Route
                path="/exam-applications"
                element={
                  <ProtectedRoute
                    exige={pode('exam_application.read', { perspectiva: 'plataforma' })}
                  >
                    <ExamApplicationPage />
                  </ProtectedRoute>
                }
              />

              {/* RiskApplication Routes */}
              <Route
                path="/risk-applications"
                element={
                  <ProtectedRoute
                    exige={pode('risk_application.read', { perspectiva: 'plataforma' })}
                  >
                    <RiskApplicationPage />
                  </ProtectedRoute>
                }
              />

              {/* SourceApplication Routes */}
              <Route
                path="/source-applications"
                element={
                  <ProtectedRoute
                    exige={pode('source_application.read', { perspectiva: 'plataforma' })}
                  >
                    <SourceApplicationPage />
                  </ProtectedRoute>
                }
              />

              {/* ApplicationGroup Routes */}
              <Route
                path="/application-groups"
                element={
                  <ProtectedRoute exige={pode('application_group.read')}>
                    <ApplicationGroupPage />
                  </ProtectedRoute>
                }
              />
              {/* RiskExamMigration Routes */}
              <Route
                path="/risk-exam-migration"
                element={
                  <ProtectedRoute exige={pode('risk_exam_migration.read')}>
                    <RiskExamMigrationPage />
                  </ProtectedRoute>
                }
              />

              {/* SourceGenerator Routes */}
              <Route
                path="/source-generators"
                element={
                  <ProtectedRoute exige={pode('source_generator.read')}>
                    <SourceGeneratorListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/source-generators/new"
                element={
                  <ProtectedRoute exige={pode('source_generator.create')}>
                    <SourceGeneratorCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/source-generators/:id/edit"
                element={
                  <ProtectedRoute exige={pode('source_generator.update')}>
                    <SourceGeneratorEditPage />
                  </ProtectedRoute>
                }
              />

              {/* MeasurementEquipment Routes */}
              <Route
                path="/measurement-equipments"
                element={
                  <ProtectedRoute exige={pode('measurement_equipment.read')}>
                    <MeasurementEquipmentListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/measurement-equipments/new"
                element={
                  <ProtectedRoute exige={pode('measurement_equipment.create')}>
                    <MeasurementEquipmentCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/measurement-equipments/:id/edit"
                element={
                  <ProtectedRoute exige={pode('measurement_equipment.update')}>
                    <MeasurementEquipmentEditPage />
                  </ProtectedRoute>
                }
              />

              {/* EnvironmentalMeasurement Routes */}
              <Route
                path="/environmental-measurements"
                element={
                  <ProtectedRoute exige={pode('environmental_measurement.read')}>
                    <EnvironmentalMeasurementListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/environmental-measurements/new"
                element={
                  <ProtectedRoute exige={pode('environmental_measurement.create')}>
                    <EnvironmentalMeasurementCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/environmental-measurements/:id/edit"
                element={
                  <ProtectedRoute exige={pode('environmental_measurement.update')}>
                    <EnvironmentalMeasurementEditPage />
                  </ProtectedRoute>
                }
              />

              {/* Training Routes */}
              <Route
                path="/trainings"
                element={
                  <ProtectedRoute exige={pode('training.read')}>
                    <TrainingListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trainings/new"
                element={
                  <ProtectedRoute exige={pode('training.create')}>
                    <TrainingCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trainings/:id/edit"
                element={
                  <ProtectedRoute exige={pode('training.update')}>
                    <TrainingEditPage />
                  </ProtectedRoute>
                }
              />

              {/* TrainingType Routes */}
              <Route
                path="/training-types"
                element={
                  <ProtectedRoute exige={pode('training-type.read')}>
                    <TrainingTypeListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training-types/new"
                element={
                  <ProtectedRoute exige={pode('training-type.create')}>
                    <TrainingTypeCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training-types/:id/edit"
                element={
                  <ProtectedRoute exige={pode('training-type.update')}>
                    <TrainingTypeEditPage />
                  </ProtectedRoute>
                }
              />

              {/* EPI Routes */}
              <Route
                path="/epis/entregas"
                element={
                  <ProtectedRoute exige={pode('epi_entrega.read')}>
                    <EntregaDeEpiListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/epis/entregas/nova"
                element={
                  <ProtectedRoute exige={pode('epi_entrega.create')}>
                    <EntregaDeEpiFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/epis/entregas/:id"
                element={
                  <ProtectedRoute exige={pode('epi_entrega.read')}>
                    <EntregaDeEpiDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/epis"
                element={
                  <ProtectedRoute exige={pode('epi.read')}>
                    <EPIListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/epis/new"
                element={
                  <ProtectedRoute exige={pode('epi.create')}>
                    <EPICreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/epis/:id/edit"
                element={
                  <ProtectedRoute exige={pode('epi.update')}>
                    <EPIEditPage />
                  </ProtectedRoute>
                }
              />

              {/* EPC Routes */}
              <Route
                path="/epcs"
                element={
                  <ProtectedRoute exige={pode('epc.read')}>
                    <EPCListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/epcs/new"
                element={
                  <ProtectedRoute exige={pode('epc.create')}>
                    <EPCCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/epcs/:id/edit"
                element={
                  <ProtectedRoute exige={pode('epc.update')}>
                    <EPCEditPage />
                  </ProtectedRoute>
                }
              />

              {/* Medical Leaves Routes */}
              <Route
                path="/medical-leaves"
                element={
                  <ProtectedRoute exige={pode('medical_leaves.read')}>
                    <MedicalLeavesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-leaves/apply"
                element={
                  <ProtectedRoute exige={pode('medical_leaves.create')}>
                    <MedicalLeavesApplyPage />
                  </ProtectedRoute>
                }
              />

              {/* Disabilities Routes */}
              <Route
                path="/disabilities"
                element={
                  <ProtectedRoute exige={pode('disability.read')}>
                    <DisabilitiesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/disabilities/new"
                element={
                  <ProtectedRoute exige={pode('disability.create')}>
                    <DisabilityFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/disabilities/:id/edit"
                element={
                  <ProtectedRoute exige={pode('disability.update')}>
                    <DisabilityFormPage />
                  </ProtectedRoute>
                }
              />

              {/* Exam Convocation Routes */}
              <Route
                path="/exam-summons"
                element={
                  <ProtectedRoute exige={pode('exam_summons.read')}>
                    <ExamConvocationPage />
                  </ProtectedRoute>
                }
              />

              {/* CID Codes Routes */}
              <Route
                path="/cid-codes"
                element={
                  <ProtectedRoute exige={pode('cid-code.read')}>
                    <CidCodesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cid-codes/new"
                element={
                  <ProtectedRoute exige={pode('cid-code.create')}>
                    <CidCodesFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cid-codes/:id/edit"
                element={
                  <ProtectedRoute exige={pode('cid-code.update')}>
                    <CidCodesFormPage />
                  </ProtectedRoute>
                }
              />

              {/* Professional Schedules Routes */}
              <Route
                path="/professional-schedules"
                element={
                  <ProtectedRoute exige={pode('professional_schedule.read')}>
                    <ProfessionalSchedulesListPage />
                  </ProtectedRoute>
                }
              />

              {/* Holidays Routes */}
              <Route
                path="/holidays"
                element={
                  <ProtectedRoute exige={pode('holiday.read')}>
                    <HolidaysListPage />
                  </ProtectedRoute>
                }
              />

              {/* WhatsApp Routes */}
              <Route
                path="/whatsapp"
                element={
                  <ProtectedRoute exige={pode('whatsapp.read')}>
                    <WhatsAppConfigPage />
                  </ProtectedRoute>
                }
              />

              {/* Certificate Routes (eSocial) */}
              <Route
                path="/certificates"
                element={
                  <ProtectedRoute exige={pode('certificate.read')}>
                    <CertificateListPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/consultants"
                element={
                  <ProtectedRoute exige={pode('consultant.read')}>
                    <ConsultantListPage />
                  </ProtectedRoute>
                }
              />

              {/* Plano de Ação (issue #359) */}
              <Route
                path="/action-plan"
                element={
                  <ProtectedRoute exige={pode('action_plan.read')}>
                    <ActionPlanListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/action-plan/:id"
                element={
                  <ProtectedRoute exige={pode('action_plan.read')}>
                    <ActionPlanDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Canal de Denúncia — gestão do comitê (conteúdo sigiloso) */}
              <Route
                path="/denuncias"
                element={
                  <ProtectedRoute exige={pode('denuncia.read')}>
                    <DenunciaListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/denuncias/:id"
                element={
                  <ProtectedRoute exige={pode('denuncia.read')}>
                    <DenunciaDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assinaturas"
                element={
                  <ProtectedRoute exige={pode('assinatura.read')}>
                    <AssinaturaListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assinaturas/:id"
                element={
                  <ProtectedRoute exige={pode('assinatura.read')}>
                    <AssinaturaDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Comercial — propostas. A rota de "nova" vem antes da de ":id" porque o
                  React Router casa na ordem: sem isso, "nova" entraria como id. */}
              <Route
                path="/proposals"
                element={
                  <ProtectedRoute exige={pode('proposal.read')}>
                    <ProposalListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/proposals/nova"
                element={
                  <ProtectedRoute exige={pode('proposal.create')}>
                    <ProposalFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/proposals/:id/documento"
                element={
                  <ProtectedRoute exige={pode('proposal.read')}>
                    <ProposalDocumentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/proposals/:id"
                element={
                  <ProtectedRoute exige={pode('proposal.update')}>
                    <ProposalFormPage />
                  </ProtectedRoute>
                }
              />

              {/* Email Configuration (SMTP credentials) */}
              <Route
                path="/settings/email"
                element={
                  <ProtectedRoute exige={pode('email.config.manage')}>
                    <EmailConfigPage />
                  </ProtectedRoute>
                }
              />
              {/* RBAC Routes - Roles */}
              <Route
                path="/roles"
                element={
                  <ProtectedRoute exige={pode('role.read')}>
                    <RoleListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roles/create"
                element={
                  <ProtectedRoute exige={pode('role.create')}>
                    <RoleFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roles/:id/edit"
                element={
                  <ProtectedRoute exige={pode('role.update')}>
                    <RoleFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roles/:id/permissions"
                element={
                  <ProtectedRoute exige={pode('role.update')}>
                    <RolePermissionsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* RBAC Routes - Permissions */}
              <Route
                path="/permissions"
                element={
                  <ProtectedRoute exige={pode('permission.read')}>
                    <PermissionListPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Support — Central de Atendimento (Adm gerencia conversas de suporte) */}
              <Route
                path="/administrativo/atendimento"
                element={
                  <ProtectedRoute exige={pode('support_conversation.view')}>
                    <SupportCenterPage />
                  </ProtectedRoute>
                }
              />

              {/* CIPAA (Election) Routes */}
              <Route
                path="/cipaa"
                element={
                  <ProtectedRoute exige={pode('cipaa.read')}>
                    <CipaaListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cipaa/new"
                element={
                  <ProtectedRoute exige={pode('cipaa.create')}>
                    <CipaaCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cipaa/:id"
                element={
                  <ProtectedRoute exige={pode('cipaa.read')}>
                    <CipaaDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cipaa/:id/edit"
                element={
                  <ProtectedRoute exige={pode('cipaa.update')}>
                    <CipaaEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cipaa/:id/candidates"
                element={
                  <ProtectedRoute exige={pode('cipaa.manage-candidates')}>
                    <CipaaDetailsPage />
                  </ProtectedRoute>
                }
              />
              {/* Public CIPAA Voting Route (no auth required) */}
              <Route
                path="/votar/:key"
                element={<CipaaPublicVotePage />}
              />
              {/* Public CIPAA Candidature Routes (no auth required) */}
              {/* Token mode - single use */}
              <Route
                path="/candidatura/:token"
                element={<CipaaPublicCandidaturePage />}
              />
              {/* AccessKey mode - public link, unlimited uses */}
              <Route
                path="/candidatura/eleicao/:key"
                element={<CipaaPublicCandidaturePage mode="accessKey" />}
              />

              {/* CAT (Comunicacao de Acidente de Trabalho) Routes */}
              <Route
                path="/cats"
                element={
                  <ProtectedRoute exige={pode('cat.read')}>
                    <CatsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cats/new"
                element={
                  <ProtectedRoute exige={pode('cat.create')}>
                    <CatCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cats/:id"
                element={
                  <ProtectedRoute exige={pode('cat.read')}>
                    <CatViewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cats/:id/edit"
                element={
                  <ProtectedRoute exige={pode('cat.update')}>
                    <CatEditPage />
                  </ProtectedRoute>
                }
              />

              {/* Psychosocial Routes */}
              {/* O COPSOQ respondido no app do Clube. Mesma permissão do painel interno: é o
                  mesmo dado psicossocial, com outra origem — quem lê um lê o outro. */}
              <Route
                path="/psychosocial/clube"
                element={
                  <ProtectedRoute exige={pode('psychosocial.read')}>
                    <PsychosocialClubeRiscoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/psychosocial"
                element={
                  <ProtectedRoute exige={pode('psychosocial.read')}>
                    <PsychosocialDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/psychosocial/forms"
                element={
                  <ProtectedRoute exige={pode('psychosocial_form.read')}>
                    <PsychosocialFormsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/psychosocial/forms/new"
                element={
                  <ProtectedRoute exige={pode('psychosocial_form.create')}>
                    <PsychosocialFormBuilderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/psychosocial/forms/:id/edit"
                element={
                  <ProtectedRoute exige={pode('psychosocial_form.update')}>
                    <PsychosocialFormBuilderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/psychosocial/campaigns"
                element={
                  <ProtectedRoute exige={pode('psychosocial.read')}>
                    <PsychosocialCampaignsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/psychosocial/campaigns/new"
                element={
                  <ProtectedRoute exige={pode('psychosocial.create')}>
                    <PsychosocialCampaignFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/psychosocial/campaigns/:id"
                element={
                  <ProtectedRoute exige={pode('psychosocial.read')}>
                    <PsychosocialCampaignDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/psychosocial/campaigns/:id/dashboard"
                element={
                  <ProtectedRoute exige={pode('psychosocial.read')}>
                    <PsychosocialCampaignDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/psychosocial/campaigns/:id/results"
                element={
                  <ProtectedRoute exige={pode('psychosocial_results.read')}>
                    <PsychosocialResultsPage />
                  </ProtectedRoute>
                }
              />
              {/* Public Psychosocial Respond Route (no auth required) */}
              <Route
                path="/psicossocial/responder/:token"
                element={<PsychosocialRespondPage />}
              />

              {/* Users Routes */}
              <Route
                path="/users"
                element={
                  <ProtectedRoute exige={pode('user.read')}>
                    <UsersListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/create"
                element={
                  <ProtectedRoute exige={pode('user.create')}>
                    <UserCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/:id"
                element={
                  <ProtectedRoute exige={pode('user.read')}>
                    <UserDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/:id/edit"
                element={
                  <ProtectedRoute exige={pode('user.update')}>
                    <UserEditPage />
                  </ProtectedRoute>
                }
              />

              {/* Financial Routes */}
              <Route
                path="/financial"
                element={
                  <ProtectedRoute exige={pode('invoices.view')}>
                    <FinancialDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financial/invoices"
                element={
                  <ProtectedRoute exige={pode('invoices.view')}>
                    <InvoiceListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financial/invoices/:id"
                element={
                  <ProtectedRoute exige={pode('invoices.view')}>
                    <InvoiceDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financial/platform-fees"
                element={
                  <ProtectedRoute exige={pode('platform-fees.view')}>
                    <PlatformFeeConfigPage />
                  </ProtectedRoute>
                }
              />

              {/* Profile Routes */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute exige={semPorta('a própria conta de quem está logado')}>
                    <UserProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute exige={semPorta('a própria senha de quem está logado')}>
                    <ChangePasswordPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/mfa"
                element={
                  <ProtectedRoute exige={semPorta('o próprio segundo fator de quem está logado')}>
                    <MfaSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* Central de Ajuda */}
              <Route
                path="/ajuda"
                element={
                  <ProtectedRoute
                    exige={semPorta('a ajuda descreve as telas do sistema, não mostra dado de ninguém')}
                  >
                    <HelpCenterPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ajuda/:entity/:screen"
                element={
                  <ProtectedRoute
                    exige={semPorta('a ajuda descreve as telas do sistema, não mostra dado de ninguém')}
                  >
                    <HelpCenterPage />
                  </ProtectedRoute>
                }
              />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/home" replace />} />

              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </Router>
        </SnackbarProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
